// Configuration centralisée des tenants (pool clients officiels).
//
// Chaque tenant représente un client "embed" qui utilise le moteur DynastyPool
// mais avec une ligue fixe, une auth spécifique, un branding propre et
// potentiellement une liste restreinte de pages accessibles.
//
// Le tenant est déterminé au runtime par tenantStore.ts (query param, sessionStorage,
// ou fallback "default"). Ne PAS étendre cette structure en gros framework
// multi-tenant : on veut une petite couche de config propre et maintenable.
//
// IMPORTANT : ne jamais dupliquer les données NHL par tenant. Les tenants ne
// changent que le league_id utilisé pour les requêtes pool-specific.

export type TenantId = 'default' | 'arsenal' | 'tslh';

export type TenantAuthMode = 'internal' | 'oidc';

export interface TenantConfig {
  /** Identifiant technique du tenant */
  id: TenantId;
  /** Nom affiché (pour debug/logs) */
  displayName: string;
  /**
   * league_id fixe pour ce tenant, ou null si le tenant peut naviguer entre
   * plusieurs ligues (cas "default" = DynastyPool grand public).
   */
  leagueId: number | null;
  /** Mode d'authentification */
  authMode: TenantAuthMode;
  /**
   * Si non-null, seules ces routes sont accessibles (menus + guard).
   * Si null, aucune restriction.
   */
  allowedPaths: readonly string[] | null;
  /**
   * Nom de la classe CSS ajoutée sur <html data-tenant="…"> pour scoper
   * le branding. Doit correspondre aux règles CSS dans index.css.
   */
  brandingClass: string | null;
}

/**
 * Pages autorisées pour TSLH (expérience volontairement limitée).
 * Utilisé par Sidebar/Navbar et par le route guard.
 */
export const TSLH_ALLOWED_PATHS: readonly string[] = [
  '/auth',
  '/build-my-team',
  '/my-teams',
  '/free-agents',
  '/players',
  '/other-teams',
  '/line-combinations',
  '/standings',
  '/followed-poolers',
  '/league-rules',
  '/league-rules-onboarding',
  '/my-office',
  '/my-profile',
  '/team-profile',
  '/maintenance-build',
] as const;

const TENANTS: Record<TenantId, TenantConfig> = {
  default: {
    id: 'default',
    displayName: 'DynastyPool',
    leagueId: null,
    authMode: 'internal',
    allowedPaths: null,
    brandingClass: null,
  },
  arsenal: {
    id: 'arsenal',
    displayName: 'Arsenal / BPM',
    leagueId: 138,
    authMode: 'oidc',
    allowedPaths: null, // Arsenal utilise déjà is_bpm_session pour filtrer
    brandingClass: null, // Arsenal a son propre système de branding existant
  },
  tslh: {
    id: 'tslh',
    displayName: 'TSLH',
    leagueId: 152,
    authMode: 'internal',
    allowedPaths: TSLH_ALLOWED_PATHS,
    brandingClass: 'tslh',
  },
};

export function getTenantConfig(id: TenantId): TenantConfig {
  return TENANTS[id];
}

/** Retourne true si le path est autorisé pour ce tenant. */
export function isPathAllowed(tenant: TenantId, path: string): boolean {
  const config = TENANTS[tenant];
  if (!config.allowedPaths) return true;
  // Autoriser les sous-routes d'un path autorisé (ex: /other-teams/:userId)
  return config.allowedPaths.some((allowed) => path === allowed || path.startsWith(`${allowed}/`));
}
